import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { fmtCurrency, CURRENCIES } from "@/lib/calc";

const pad = (n) => String(n).padStart(2, "0");
const to12 = (hm) => { const p = (hm || "0:0").split(":"); const H = Number(p[0]); const M = Number(p[1]); return `${(H % 12) || 12}:${pad(M)} ${H >= 12 ? "PM" : "AM"}`; };
const addMin = (hm, mins) => { const p = (hm || "09:00").split(":"); const t = Number(p[0]) * 60 + Number(p[1]) + mins; return `${pad(Math.floor(t / 60) % 24)}:${pad(((t % 60) + 60) % 60)}`; };
export const fmtDur = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}` : `${m}m`);

export const rowDuration = (r, services) => (r.duration !== "" && r.duration != null ? (parseInt(r.duration) || 0) : (services.find((s) => s.id === r.service_id)?.duration_min || 0));
export const rowPrice = (r, services) => (r.price !== "" && r.price != null ? (parseFloat(r.price) || 0) : (services.find((s) => s.id === r.service_id)?.price || 0));

// Re-flow start times so each service begins when the previous one ends (from index `from` onward).
export const chainRows = (rows, services, from = 1, anchor = null) => rows.reduce((acc, r, i) => {
  if (i < from && !(i === 0 && anchor)) return [...acc, r];
  const prev = i === 0 ? anchor : acc[i - 1];
  if (!prev) return [...acc, r];
  return [...acc, { ...r, time: addMin(prev.time, rowDuration(prev, services)) }];
}, []);

const ServicePicker = ({ value, onChange, services, svcGroups, cur, testId, color }) => {
  const [open, setOpen] = useState(false);
  const cur_ = services.find((s) => s.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" data-testid={testId}
          className="h-10 w-full min-w-0 rounded-lg border border-input bg-background pl-3 pr-2 text-left text-sm flex items-center gap-2"
          style={{ borderLeft: `4px solid ${color}` }}>
          <span className={`flex-1 truncate ${cur_ ? "" : "text-muted-foreground"}`}>{cur_ ? cur_.name : "Search service…"}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]" align="start">
        <Command>
          <CommandInput placeholder="Type a service…" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No services found.</CommandEmpty>
            {svcGroups.map(([cat, items]) => (
              <CommandGroup key={cat} heading={cat}>
                {items.map((s) => (
                  <CommandItem key={s.id} value={s.name} onSelect={() => { onChange(s.id); setOpen(false); }} data-testid={`${testId}-opt-${s.id}`}>
                    <Check className={`mr-2 h-4 w-4 shrink-0 ${value === s.id ? "opacity-100" : "opacity-0"}`} />
                    <span className="flex-1 truncate">{s.name}</span>
                    <span className="text-xs text-muted-foreground ml-2 shrink-0">{s.duration_min}m · {fmtCurrency(s.price, cur, 0)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const ServiceLine = ({ row, onChange, onRemove, services, svcGroups, staff, times, cur, isAdmin, color, testId, lockTime }) => {
  const svc = services.find((s) => s.id === row.service_id);
  const sym = CURRENCIES[cur]?.symbol || "$";
  return (
    <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(0,1fr)_118px_104px_64px_80px_32px] gap-1.5 items-center" data-testid={testId}>
      <ServicePicker value={row.service_id} onChange={(v) => onChange({ service_id: v, duration: "", price: "" })} services={services} svcGroups={svcGroups} cur={cur} testId={`${testId}-service`} color={color} />
      <button type="button" onClick={onRemove} disabled={!onRemove} className={`sm:order-last h-10 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive ${onRemove ? "" : "opacity-0 pointer-events-none"}`} data-testid={`${testId}-remove`} aria-label="Remove service"><X className="h-4 w-4" /></button>
      <Select value={row.staff_id || ""} onValueChange={(v) => onChange({ staff_id: v })}>
        <SelectTrigger className="h-10 rounded-lg text-xs sm:text-sm" data-testid={`${testId}-staff`}><SelectValue placeholder="Staff" /></SelectTrigger>
        <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={row.time} onValueChange={(v) => onChange({ time: v })} disabled={lockTime}>
        <SelectTrigger className="h-10 rounded-lg text-xs sm:text-sm tabular-nums" data-testid={`${testId}-time`}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-64">{times.map((t) => <SelectItem key={t} value={t}>{to12(t)}</SelectItem>)}</SelectContent>
      </Select>
      <div className="relative">
        <Input type="number" min="0" step="5" value={row.duration ?? ""} onChange={(e) => onChange({ duration: e.target.value })} placeholder={String(svc?.duration_min || 30)} className="h-10 rounded-lg pr-6 tabular-nums text-xs sm:text-sm" data-testid={`${testId}-duration`} />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground pointer-events-none">m</span>
      </div>
      {isAdmin ? (
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{sym}</span>
          <Input type="number" min="0" value={row.price ?? ""} onChange={(e) => onChange({ price: e.target.value })} placeholder={String(svc?.price ?? "")} className="h-10 rounded-lg pl-5 tabular-nums text-xs sm:text-sm" data-testid={`${testId}-price`} />
        </div>
      ) : <div className="h-10 flex items-center justify-end text-sm tabular-nums text-muted-foreground pr-1">{svc ? fmtCurrency(svc.price, cur, 0) : ""}</div>}
    </div>
  );
};

/** Timely-style service list: one line per service, chained start times, totals. */
export const ServiceLines = ({ rows, onChange, services, svcGroups, staff, times, cur, isAdmin, colorFor, testId = "svc-lines", minRows = 1, anchor }) => {
  const update = (i, patch) => {
    const next = rows.map((r, j) => (j === i ? { ...r, ...patch } : r));
    const reflowFrom = ("time" in patch || "duration" in patch || "service_id" in patch) ? i + 1 : rows.length;
    onChange(chainRows(next, services, reflowFrom, null));
  };
  const add = () => {
    const last = rows[rows.length - 1] || anchor;
    const time = last ? addMin(last.time, rowDuration(last, services)) : "09:00";
    onChange([...rows, { service_id: "", staff_id: last?.staff_id || staff[0]?.id || "", time, duration: "", price: "" }]);
  };
  const remove = (i) => onChange(chainRows(rows.filter((_, j) => j !== i), services, Math.max(1, i), i === 0 ? anchor : null));
  const filled = rows.filter((r) => r.service_id);
  const totalMin = filled.reduce((t, r) => t + rowDuration(r, services), 0) + (anchor ? rowDuration(anchor, services) : 0);
  const totalPrice = filled.reduce((t, r) => t + rowPrice(r, services), 0) + (anchor ? rowPrice(anchor, services) : 0);
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_118px_104px_64px_80px_32px] gap-1.5 text-[11px] font-medium text-muted-foreground px-0.5">
        <span>Service</span><span>Staff</span><span>Start</span><span>Mins</span><span>Price</span><span />
      </div>
      {rows.map((r, i) => (
        <ServiceLine key={i} row={r} onChange={(p) => update(i, p)} onRemove={rows.length > minRows ? () => remove(i) : null}
          services={services} svcGroups={svcGroups} staff={staff} times={times} cur={cur} isAdmin={isAdmin}
          color={colorFor?.(r.service_id) || "var(--primary)"} testId={`${testId}-row-${i}`} />
      ))}
      <div className="flex items-center justify-between pt-1">
        <button type="button" onClick={add} className="text-sm text-primary flex items-center gap-1.5 hover:underline font-medium" data-testid={`${testId}-add`}><Plus className="h-4 w-4" />Add service</button>
        <div className="text-sm text-muted-foreground flex items-center gap-4" data-testid={`${testId}-totals`}>
          <span>Total <b className="text-foreground tabular-nums">{fmtDur(totalMin)}</b></span>
          <span><b className="text-foreground tabular-nums">{fmtCurrency(totalPrice, cur, 0)}</b></span>
        </div>
      </div>
    </div>
  );
};
